import type { OverlayScrollState } from "@cuekards/core/ipc";
import type { RecordingStatus } from "@cuekards/core/recording";
import { RECORDING_PHASE_BADGE, formatElapsed } from "@cuekards/core/recording";
import { SCROLL_MODE_LABEL } from "@cuekards/core/scroll-mode";
import {
  CONFIRM_PHRASE,
  STOP_CONFIRM_QUESTION,
} from "@cuekards/core/voice-commands";
import { BackToTopIcon } from "./icons.js";
import { useLiveElapsed } from "./use-live-elapsed.js";

/**
 * The things drawn over the prompt rather than in it, shared by the two roots
 * that draw a prompter (one-window ticket 05).
 *
 * They were all inside `OverlayApp` while that was the only prompter there was.
 * The desktop's one window puts the same prompt inside a shell, and most of these
 * belong to the prompter pane while `StopConfirmPrompt` belongs to the shell — a
 * spoken stop is a question with a five-second deadline, and it must be visible
 * from the settings pane too. Split out rather than copied, because two sets of
 * wording about a take that is about to end is the drift this package exists to
 * prevent.
 *
 * **Not everything here is shared any more.** `PrompterCorner` and
 * `BackToTopButton` are the desktop's (scroll-and-bar ticket 01): the web tab has
 * its own controls and keeps them. `RecordingBadge` stays exported bare for the
 * web root, which draws it on its own, and its absolute positioning is still what
 * places it there — the corner row overrides that for the desktop rather than the
 * badge losing it.
 */

/**
 * The always-visible prompter status (FR-28): the current scroll mode — constant
 * mode also showing its live WPM — and an off-script indicator that appears only
 * while the follower is holding position (FR-33), which is what tells the reader
 * the prompter is waiting for them rather than broken. Rendered independently of
 * the script so it is present even before a doc loads.
 */
export function StatusBar({ scroll }: { scroll: OverlayScrollState }): JSX.Element {
  const modeLabel = statusModeLabel(scroll);
  return (
    <div className="overlay-status">
      <span
        className={
          scroll.mode === "speech" && scroll.speechUnavailable
            ? "status-mode unavailable"
            : "status-mode"
        }
      >
        {modeLabel}
      </span>
      {scroll.offScript && <span className="status-offscript">OFF-SCRIPT</span>}
    </div>
  );
}

/**
 * The mode chip's text. Constant mode carries its live rate; speech mode says so
 * when it cannot actually run, because a chip reading `SPEECH` over a prompter that
 * will never move is the one thing goal G5 forbids — the fallback modes are a
 * keystroke away, but only if the user knows they need them.
 */
function statusModeLabel(scroll: OverlayScrollState): string {
  if (scroll.mode === "constant" && scroll.wpm > 0) {
    return `${SCROLL_MODE_LABEL.constant} · ${scroll.wpm} WPM`;
  }
  if (scroll.mode === "speech" && scroll.speechUnavailable) {
    return `${SCROLL_MODE_LABEL.speech} · UNAVAILABLE`;
  }
  return SCROLL_MODE_LABEL[scroll.mode];
}

/**
 * The prompter's top-right corner, which two things now want (scroll-and-bar
 * ticket 01).
 *
 * A row rather than two absolutely-positioned elements racing for the same
 * coordinates. The badge grows and shrinks — `REC · 00:42` is wider than
 * `SAVING · 00:00` at some fonts and narrower at others — so any arrangement that
 * pins both by their own `right` is one long take away from overlapping. Laying
 * them out puts the question to the browser instead, and the button is first so
 * it stays in the same place whether or not a take is rolling.
 *
 * The row itself is inert; each child re-enables what it needs. The badge must
 * never intercept a click, and the button is a control.
 */
export function PrompterCorner({
  status,
}: {
  status: RecordingStatus;
}): JSX.Element {
  return (
    <div className="overlay-topright">
      <BackToTopButton />
      <RecordingBadge status={status} />
    </div>
  );
}

/**
 * Back to the top of the script, over the script it moves (scroll-and-bar ticket
 * 01).
 *
 * It was a labelled button in the follow strip, and the strip is gone. What it
 * does has not changed and neither has the reason it exists: rehearsal follows you
 * down the script, the way a rehearsal ends is halfway down, and the alternative
 * ways back are nudging line by line or reloading the doc — which re-fetches it
 * and rebuilds the recogniser, a much heavier promise than "start again".
 *
 * **Deliberately not disabled by anything**, including mid-take. It moves the
 * anchor, which is a thing the prompter can always do, and the one moment you most
 * want it is a take you have decided to restart from the top.
 *
 * The title says *start again* rather than *scroll*, and that wording is now
 * load-bearing: once the pane scrolls (ticket 03), dragging the thumb to the top
 * moves your eyes and this moves your place. They look identical and are not.
 */
export function BackToTopButton(): JSX.Element {
  return (
    <button
      type="button"
      className="overlay-back-to-top"
      aria-label="Back to top"
      title="Start again from the top of the script"
      onClick={() => window.cuekards.restartPosition()}
    >
      <BackToTopIcon />
    </button>
  );
}

/**
 * Whether you are rolling, stated on the prompter at all times (FR-49).
 *
 * It sits in the top corner rather than beside the mode chip because it has to
 * be readable at filming distance — this is the thing you glance at from three
 * metres away to check the take is running, and getting it wrong costs a
 * performance. Each phase gets its own colour and word: a pulsing red dot while
 * rolling, amber and still while paused, so "paused" can never be mistaken for
 * "recording" at a glance. Nothing at all is drawn when idle, so the prompter
 * stays clean while rehearsing.
 */
export function RecordingBadge({
  status,
}: {
  status: RecordingStatus;
}): JSX.Element | null {
  const elapsed = useLiveElapsed(status);
  // Nothing during the countdown either: it draws its own number across the
  // whole monitor, and a small word beside it would only compete with it.
  if (status.phase === "idle" || status.phase === "counting") return null;
  return (
    <div className={`overlay-recording phase-${status.phase}`} role="status">
      <span className="rec-dot" />
      <span className="rec-label">{RECORDING_PHASE_BADGE[status.phase]}</span>
      <span className="rec-time">{formatElapsed(elapsed)}</span>
    </div>
  );
}

/**
 * A spoken stop, waiting for the confirmation that would carry it out (FR-56).
 *
 * Across the middle of whatever it is drawn in rather than tucked in a corner
 * with the other indicators, because unlike them it is a question with a
 * deadline: the user has five seconds, they are looking at the script and not at
 * the desk, and a prompt they do not notice is the same as no prompt at all. It
 * says the exact phrase to say, since the whole point is that it is not the one
 * just said.
 *
 * There is nothing to dismiss and no timer drawn. Saying nothing *is* the answer,
 * and the prompt goes when the window does.
 */
export function StopConfirmPrompt(): JSX.Element {
  return (
    <div className="overlay-confirm" role="alert">
      <span className="confirm-question">{STOP_CONFIRM_QUESTION}</span>
      <span className="confirm-phrase">say “{CONFIRM_PHRASE}”</span>
    </div>
  );
}
